// src/pages/rag/RagCollectionDetailPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ragListCollections,
  ragUploadDocuments,
  ragStartIndex,
  ragIndexProgress,
  ragListJobs,
} from "../api/client";
import "../styles/rag.css";
import "../styles/base.css";

export default function RagCollectionDetailPage() {
  const { id } = useParams();
  const [kb, setKb] = useState(null);

  const [files, setFiles] = useState([]);
  const [serverPaths, setServerPaths] = useState("");
  const [urls, setUrls] = useState("");

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobs, setJobs] = useState([]);

  const pollTimer = useRef(null);

  const loadKb = async () => {
    const list = await ragListCollections();
    const found = list.find((c) => String(c.id) === String(id));
    setKb(found || null);
  };

  const loadJobs = async () => {
    try {
      const data = await ragListJobs(id);
      setJobs(data);
    } catch (e) {
      console.error("Failed to load jobs", e);
    }
  };

  useEffect(() => {
    loadKb();
    loadJobs();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const computePercent = (data) => {
    const { stage, total_docs, processed_docs, total_chunks, processed_chunks } =
      data;

    if (stage === "idle") return 0;
    if (stage === "queued") return 5;

    if (stage === "chunking") {
      if (total_docs > 0) {
        return Math.min(
          30,
          Math.round((processed_docs / total_docs) * 30)
        );
      }
      return 10;
    }

    if (stage === "embedding" || stage === "indexing") {
      if (total_chunks > 0) {
        const frac = processed_chunks / total_chunks;
        return Math.min(99, 30 + Math.round(frac * 69));
      }
      return 40;
    }

    if (stage === "done") return 100;
    if (stage === "error") return 0;

    return 0;
  };

  const startPolling = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);

    pollTimer.current = setInterval(async () => {
      try {
        const data = await ragIndexProgress(id);
        const pct = computePercent(data);

        setStage(data.stage || "idle");
        setStatus(data.message || "");
        setProgress(pct);
        if (data.job_id) setCurrentJobId(data.job_id);

        // refresh jobs so status updates
        loadJobs();

        if (data.stage === "done" || data.stage === "error") {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setBusy(false);
        }
      } catch (err) {
        console.error(err);
        clearInterval(pollTimer.current);
        pollTimer.current = null;
        setBusy(false);
        setStage("error");
        setStatus(
          `Error while checking progress: ${
            err?.message || "Unknown error"
          }`
        );
        setProgress(0);
      }
    }, 2000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!kb) return;

    setBusy(true);
    setStage("upload");
    setStatus("Uploading documents...");
    setProgress(10);

    try {
      // 1) upload files / paths / URLs
      await ragUploadDocuments(id, {
        files,
        serverPaths,
        urls,
      });

      setStage("chunking");
      setStatus("Documents uploaded. Starting indexing pipeline...");
      setProgress(20);

      // 2) start background index job (creates a job row)
      const res = await ragStartIndex(id);
      if (res?.job_id) {
        setCurrentJobId(res.job_id);
      }

      await loadJobs();
      // 3) poll for progress
      startPolling();
    } catch (err) {
      console.error(err);
      setBusy(false);
      setStage("error");
      setStatus(
        `Error while uploading/indexing: ${
          err?.message || "Unknown error (check backend logs)"
        }`
      );
      setProgress(0);
    }
  };

  const stageLabel = (() => {
    switch (stage) {
      case "upload":
        return "Uploading documents";
      case "queued":
        return "Queued for indexing";
      case "chunking":
        return "Chunking documents";
      case "embedding":
        return "Generating embeddings";
      case "indexing":
        return "Building FAISS index";
      case "done":
        return "Completed";
      case "error":
        return "Error";
      default:
        return "Idle";
    }
  })();

  if (!kb) {
    return <div>Loading KB...</div>;
  }

  return (
    <div>
      <h1>{kb.name}</h1>
      <p>{kb.description}</p>
      <p>
        <strong>Slug:</strong> {kb.slug}
      </p>

      <section className="rag-section">
        <h2>Ingest Documents into KB</h2>
        <form className="rag-form" onSubmit={onSubmit}>
          <div>
            <label>Upload files</label>
            <input type="file" multiple onChange={handleFilesChange} />
            {files.length > 0 && (
              <small>{files.length} file(s) selected</small>
            )}
          </div>

          <div>
            <label>Server paths (local/shared), one per line</label>
            <textarea
              rows={3}
              value={serverPaths}
              onChange={(e) => setServerPaths(e.target.value)}
              placeholder={`E:\\SOWDOCS\\clientA\\sows\\q1.docx\n/mnt/shared/contracts/master.pdf`}
            />
          </div>

          <div>
            <label>Remote URLs (http/https), one per line</label>
            <textarea
              rows={3}
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder={`https://client.com/sow-templates/master.html\nhttps://sharepoint/...`}
            />
          </div>
            <div className="rag-tabs">
          <button type="submit" disabled={busy}>
            {busy ? "Processing..." : "Upload & Build Index"}
          </button></div>
        </form>

        <div className="rag-progress-wrapper">
          <div className="rag-progress-track">
            <div
              className="rag-progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="rag-progress-label">
            {progress}% –{" "}
            {status || "No indexing in progress."}
          </div>
          <div className="rag-stage-label">
            Current step: <strong>{stageLabel}</strong>
            {currentJobId && (
              <> &nbsp; (Job #{currentJobId})</>
            )}
          </div>
        </div>
      </section>

      <section className="rag-section" style={{ marginTop: "1.5rem" }}>
        <h2>Index Jobs</h2>
        <table className="rag-table">
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Status</th>
              <th>Stage</th>
              <th>Docs</th>
              <th>Chunks</th>
              <th>Started</th>
              <th>Finished</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id}>
                <td>{job.id}</td>
                <td>{job.status}</td>
                <td>{job.stage}</td>
                <td>
                  {job.processed_docs}/{job.total_docs}
                </td>
                <td>
                  {job.processed_chunks}/{job.total_chunks}
                </td>
                <td>
                  {job.started_at
                    ? new Date(job.started_at).toLocaleString()
                    : "-"}
                </td>
                <td>
                  {job.finished_at
                    ? new Date(job.finished_at).toLocaleString()
                    : "-"}
                </td>
                <td style={{ maxWidth: 260 }}>
                  <small>{job.message}</small>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr>
                <td colSpan={8}>No jobs yet for this KB.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
